import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkReplaceUrl } from '../../api/channelBulkAdvanced';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

function normalizeBackendCat(cat: string | null | undefined): string {
  if (cat == null || cat === '') return 'UNCATEGORIZED';
  const v = String(cat).trim().toUpperCase();
  if (v === 'UNCATEGORIZED') return 'UNCATEGORIZED';
  return v;
}

export default function UrlEditorModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId, playlistName } = usePlaylist();

  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [search, setSearch] = useState('');
  const [replace, setReplace] = useState('');
  const [target, setTarget] = useState<'url' | 'logo'>('url');
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [catPick, setCatPick] = useState<Record<string, boolean>>({});
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of channels) s.add(normalizeBackendCat(c.category));
    return [...s].sort((a, b) => a.localeCompare(b, 'en'));
  }, [channels]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(rows);
      const init: Record<string, boolean> = {};
      for (const c of rows) init[normalizeBackendCat(c.category)] = true;
      setCatPick(init);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to load channels'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    void load();
  }, [show, load]);

  const rowsInScope = useCallback((): ChannelRow[] => {
    if (scope === 'all') return channels;
    if (scope === 'selected') {
      const set = new Set(selectedIds.map(Number));
      return channels.filter((c) => set.has(Number(c.id)));
    }
    const cats = Object.entries(catPick)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (cats.length === 0) return [];
    const cset = new Set(cats);
    return channels.filter((c) => cset.has(normalizeBackendCat(c.category)));
  }, [channels, scope, selectedIds, catPick]);

  const summary = useMemo(() => {
    const q = search.trim();
    if (scope === 'categories' && Object.values(catPick).every((v) => !v)) {
      return { text: 'Select at least one category.', kind: 'warning' as const };
    }
    if (scope === 'selected') {
      if (!gridActive) {
        return {
          text: 'Open the Basic editor for this playlist and select channels in the grid.',
          kind: 'warning' as const,
        };
      }
      if (selectedIds.length === 0) {
        return { text: 'No channels selected in the Basic editor.', kind: 'warning' as const };
      }
    }
    if (!q) return { text: 'Enter text to search to see the preview.', kind: 'secondary' as const };
    const rows = rowsInScope();
    let n = 0;
    const field = target;
    for (const c of rows) {
      const v = c[field] == null ? '' : String(c[field]);
      if (v.includes(q)) n++;
    }
    const fieldLabel = field === 'logo' ? 'logo' : 'stream';
    return {
      text: `In scope: ${rows.length} channel(s). Matches to replace (field ${fieldLabel}): ${n}.`,
      kind: n > 0 ? ('info' as const) : ('secondary' as const),
    };
  }, [search, scope, target, gridActive, catPick, rowsInScope, selectedIds]);

  async function apply() {
    if (!playlistId) {
      showToast('Select a playlist (e.g. from Playlist manager).', 'warning');
      return;
    }
    const q = search.trim();
    if (!q) {
      showToast('Please fill in the “Search” field.', 'warning');
      return;
    }
    if (scope === 'selected') {
      if (!gridActive) {
        showToast('Open the Basic editor and select channels.', 'warning');
        return;
      }
      if (selectedIds.length === 0) {
        showToast('Select at least one channel in the Basic editor.', 'warning');
        return;
      }
    }
    const catsChosen = Object.entries(catPick)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (scope === 'categories' && catsChosen.length === 0) {
      showToast('Select at least one category.', 'warning');
      return;
    }

    const rows = rowsInScope();
    let matchCount = 0;
    for (const c of rows) {
      const v = c[target] == null ? '' : String(c[target]);
      if (v.includes(q)) matchCount++;
    }
    if (matchCount === 0) {
      showToast('No channels in the selected scope contain the given text.', 'warning');
      return;
    }

    const undoValuesById: Record<string, string> = {};
    const redoValuesById: Record<string, string> = {};
    for (const c of rows) {
      const id = Number(c.id);
      if (!Number.isFinite(id)) continue;
      const oldVal = c[target] == null ? '' : String(c[target]);
      if (!oldVal.includes(q)) continue;
      const newVal = oldVal.split(q).join(replace);
      if (newVal === oldVal) continue;
      undoValuesById[id] = oldVal;
      redoValuesById[id] = newVal;
    }

    const channel_ids = scope === 'selected' ? selectedIds.map(Number) : [];
    const categories = scope === 'categories' ? catsChosen : [];

    setBusy(true);
    try {
      const res = await bulkReplaceUrl({
        playlist_id: parseInt(playlistId, 10),
        search: q,
        replace,
        target,
        scope,
        channel_ids,
        categories,
      });
      if (!res.success) {
        showToast(res.message || 'Operation failed.', 'error');
        return;
      }
      showToast(res.message || 'Updated.', 'success');
      if (Object.keys(redoValuesById).length > 0) {
        record({
          label: 'URL editor',
          undo: {
            type: 'setField',
            playlist_id: parseInt(playlistId, 10),
            field: target,
            valuesById: undoValuesById,
          },
          redo: {
            type: 'setField',
            playlist_id: parseInt(playlistId, 10),
            field: target,
            valuesById: redoValuesById,
          },
        });
      }
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title={
        <span>
          <i className="fas fa-link me-2" />
          URL editor
        </span>
      }
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          {step === 2 ? (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setStep(1)}>
              Previous
            </button>
          ) : null}
          {step === 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => {
                if (!search.trim()) {
                  showToast('Please fill in the “Search” field.', 'warning');
                  return;
                }
                setStep(2);
              }}
            >
              Next
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void apply()}>
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Apply
                </>
              ) : (
                'Apply'
              )}
            </button>
          )}
        </div>
      }
    >
      <p className="text-muted small mb-2">
        Playlist: <strong>{playlistName || '—'}</strong>
      </p>
      <p className="small mb-3">
        Search for a fragment in stream URLs or logos and replace it with another one. Works only for the{' '}
        <strong>currently selected playlist</strong>.
      </p>

      {step === 1 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 1</p>
          <div className="mb-3">
            <label className="form-label small text-muted mb-1">Search</label>
            <input
              className="form-control font-monospace"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. https://old-host.com/"
              autoComplete="off"
            />
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted mb-1">Replace with</label>
            <input
              className="form-control font-monospace"
              value={replace}
              onChange={(e) => setReplace(e.target.value)}
              placeholder="e.g. https://new-host.com/"
              autoComplete="off"
            />
          </div>

          <p className="small fw-semibold text-uppercase text-muted mb-2">Search in:</p>
          <div className="mb-0">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="ueTurl"
                checked={target === 'url'}
                onChange={() => setTarget('url')}
              />
              <label className="form-check-label" htmlFor="ueTurl">
                Stream url
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="ueTlogo"
                checked={target === 'logo'}
                onChange={() => setTarget('logo')}
              />
              <label className="form-check-label" htmlFor="ueTlogo">
                Logo url
              </label>
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 2</p>
          <div className="mb-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="ueSsel"
                checked={scope === 'selected'}
                onChange={() => setScope('selected')}
              />
              <label className="form-check-label" htmlFor="ueSsel">
                {selectedIds.length} selected stream{selectedIds.length === 1 ? '' : 's'}
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="ueScat"
                checked={scope === 'categories'}
                onChange={() => setScope('categories')}
              />
              <label className="form-check-label" htmlFor="ueScat">
                Streams from categories
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="ueSall"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              <label className="form-check-label" htmlFor="ueSall">
                All streams
              </label>
            </div>
          </div>

          {scope === 'categories' ? (
            <div className="border rounded p-2 mb-3 bg-light bg-opacity-50" style={{ maxHeight: 180, overflowY: 'auto' }}>
              <div className="small text-muted mb-2">Select categories:</div>
              <div className="d-flex flex-column gap-1">
                {categories.map((c) => {
                  const display = c.toUpperCase();
                  return (
                    <div className="form-check" key={c}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`uec-${c}`}
                        checked={!!catPick[c]}
                        onChange={(e) => setCatPick((p) => ({ ...p, [c]: e.target.checked }))}
                      />
                      <label className="form-check-label small" htmlFor={`uec-${c}`}>
                        {display}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className={`alert alert-${summary.kind === 'warning' ? 'warning' : summary.kind === 'info' ? 'info' : 'secondary'} py-2 small mb-0`}>
            {summary.text}
          </div>
        </>
      )}
    </SimpleModal>
  );
}
